"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useEffectEvent, useId, useRef, useState, type CSSProperties } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Typography } from "@repo/ui/aops-components/typography";
import { sendDiscoveryEventBeacon } from "@/lib/discoveryEventBeacon";
import { ProgramSubjectNavItem as SubjectNavItem, type ProgramSubjectNavImage, type ProgramSubjectNavItemData } from "./ProgramSubjectNavItem";
import styles from "./ProgramSubjectNav.module.css";
export type { ProgramSubjectNavImage };
/** Nav item shape — named for existing imports from this module. */
export type ProgramSubjectNavItem = ProgramSubjectNavItemData;
type ProgramSubjectNavProps = {
  filterTitle?: string | null;
  items: ProgramSubjectNavItemData[];
  programSlug: string;
};
const FALLBACK_FILTER_TITLE = "Explore Subjects";
const DROPDOWN_FIELD_LABEL = "Subject";
function sectionIdForKey(key: string) {
  return `subject-${key}`;
}
function scrollToSubject(key: string) {
  const href = `#${sectionIdForKey(key)}`;
  const section = document.getElementById(sectionIdForKey(key));
  if (!section) {
    return false;
  }
  section.scrollIntoView({
    behavior: "smooth",
    block: "start"
  });
  window.history.replaceState(null, "", href);
  return true;
}
function keyFromSectionId(id: string) {
  return id.replace(/^subject-/, "");
}
export function ProgramSubjectNav(t0) {
  const $ = _c(49);
  const {
    filterTitle,
    items,
    programSlug
  } = t0;
  const [activeKey, setActiveKey] = useState(items[0]?.key ?? "");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownListId = useId();
  const dropdownRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const intersectingKeysRef = useRef(t1);
  const programmaticScrollTokenRef = useRef(0);
  const suppressObserverRef = useRef(false);
  const programmaticScrollTimeoutRef = useRef(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (intersectingKeysRef.current.size === 0) {
        return;
      }
      const stickyOffset = dropdownRef.current?.getBoundingClientRect().height || 56;
      let nextKey = "";
      let bestScore = Number.POSITIVE_INFINITY;
      for (const key of intersectingKeysRef.current) {
        const section = document.getElementById(sectionIdForKey(key));
        if (!section) {
          continue;
        }
        const top = section.getBoundingClientRect().top;
        const distance = Math.abs(top - stickyOffset);
        const score = top <= stickyOffset ? distance : distance + 1000;
        if (score < bestScore) {
          bestScore = score;
          nextKey = key;
        }
      }
      if (nextKey) {
        setActiveKey(nextKey);
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const applyActiveFromIntersections = useEffectEvent(t2);
  let t3;
  if ($[2] !== applyActiveFromIntersections) {
    t3 = entries => {
      for (const entry of entries) {
        const key_0 = keyFromSectionId(entry.target.id);
        if (!key_0) {
          continue;
        }
        if (entry.isIntersecting) {
          intersectingKeysRef.current.add(key_0);
        } else {
          intersectingKeysRef.current.delete(key_0);
        }
      }
      if (suppressObserverRef.current) {
        return;
      }
      applyActiveFromIntersections();
    };
    $[2] = applyActiveFromIntersections;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const syncActiveFromEntries = useEffectEvent(t3);
  const endProgrammaticScroll = token => {
    if (programmaticScrollTokenRef.current !== token) {
      return;
    }
    if (programmaticScrollTimeoutRef.current != null) {
      window.clearTimeout(programmaticScrollTimeoutRef.current);
      programmaticScrollTimeoutRef.current = null;
    }
    suppressObserverRef.current = false;
  };
  let t4;
  if ($[4] !== items || $[5] !== syncActiveFromEntries) {
    t4 = () => {
      if (items.length === 0) {
        return;
      }
      const sections = items.map(_temp).filter(_temp2);
      if (sections.length === 0) {
        return;
      }
      const intersectingKeys = intersectingKeysRef.current;
      intersectingKeys.clear();
      const observer = new IntersectionObserver(syncActiveFromEntries, {
        rootMargin: "-72px 0px -55% 0px",
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1]
      });
      for (const section_1 of sections) {
        observer.observe(section_1);
      }
      return () => {
        observer.disconnect();
        intersectingKeys.clear();
      };
    };
    $[4] = items;
    $[5] = syncActiveFromEntries;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== items) {
    t5 = [items];
    $[7] = items;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => () => {
      if (programmaticScrollTimeoutRef.current != null) {
        window.clearTimeout(programmaticScrollTimeoutRef.current);
      }
    };
    t7 = [];
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[11] !== dropdownOpen) {
    t8 = () => {
      if (!dropdownOpen) {
        return;
      }
      const handlePointerDown = event => {
        const target = event.target;
        if (!(target instanceof Node)) {
          return;
        }
        if (!dropdownRef.current?.contains(target)) {
          setDropdownOpen(false);
        }
      };
      const handleKeyDown = event_0 => {
        if (event_0.key === "Escape") {
          setDropdownOpen(false);
        }
      };
      document.addEventListener("mousedown", handlePointerDown);
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("mousedown", handlePointerDown);
        document.removeEventListener("keydown", handleKeyDown);
      };
    };
    t9 = [dropdownOpen];
    $[11] = dropdownOpen;
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  useEffect(t8, t9);
  if (items.length === 0) {
    return null;
  }
  let t10;
  if ($[14] !== filterTitle) {
    t10 = filterTitle?.trim() || FALLBACK_FILTER_TITLE;
    $[14] = filterTitle;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const label = t10;
  let t11;
  if ($[16] !== activeKey || $[17] !== items) {
    t11 = items.find(item_0 => item_0.key === activeKey) ?? items[0] ?? null;
    $[16] = activeKey;
    $[17] = items;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const activeItem = t11;
  const selectSubject = key_1 => {
    sendDiscoveryEventBeacon("program_subject_selected", {
      subject_key: key_1,
      program_slug: programSlug
    });
    setActiveKey(key_1);
    setDropdownOpen(false);
    const token_0 = programmaticScrollTokenRef.current + 1;
    programmaticScrollTokenRef.current = token_0;
    suppressObserverRef.current = true;
    if (programmaticScrollTimeoutRef.current != null) {
      window.clearTimeout(programmaticScrollTimeoutRef.current);
      programmaticScrollTimeoutRef.current = null;
    }
    const didScroll = scrollToSubject(key_1);
    if (!didScroll) {
      endProgrammaticScroll(token_0);
      return;
    }
    let sawScroll = false;
    const finish = () => {
      window.removeEventListener("scrollend", finish);
      window.removeEventListener("scroll", onScroll);
      endProgrammaticScroll(token_0);
    };
    const onScroll = () => {
      if (programmaticScrollTokenRef.current !== token_0) {
        window.removeEventListener("scroll", onScroll);
        return;
      }
      sawScroll = true;
      if (programmaticScrollTimeoutRef.current != null) {
        window.clearTimeout(programmaticScrollTimeoutRef.current);
      }
      programmaticScrollTimeoutRef.current = window.setTimeout(finish, 150);
    };
    window.addEventListener("scrollend", finish, {
      once: true
    });
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    programmaticScrollTimeoutRef.current = window.setTimeout(() => {
      if (programmaticScrollTokenRef.current === token_0 && !sawScroll) {
        finish();
      }
    }, 100);
  };
  const t12 = styles;
  let t13;
  if ($[19] !== label) {
    t13 = <Typography variant="eyebrow" className={styles.title}>{label}</Typography>;
    $[19] = label;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = items.map(item_1 => <SubjectNavItem key={item_1.key} item={item_1} isActive={item_1.key === activeKey} href={`#${sectionIdForKey(item_1.key)}`} onSelect={selectSubject} />);
  let t15;
  if ($[21] !== t14) {
    t15 = <ul className={styles.list}>{t14}</ul>;
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t13 || $[24] !== t15) {
    t16 = <div className={styles.desktopNav}>{t13}{t15}</div>;
    $[23] = t13;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const t17 = styles;
  const t18 = dropdownOpen ? `${styles.dropdownTrigger} ${styles.dropdownTriggerOpen}` : styles.dropdownTrigger;
  let t19;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setDropdownOpen(_temp3);
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Typography as="span" variant="tag" className={styles.dropdownFieldLabel}>{DROPDOWN_FIELD_LABEL}</Typography>;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  const t21 = activeItem?.title;
  let t22;
  if ($[28] !== t21) {
    t22 = <Typography as="span" variant="buttonLg" className={styles.dropdownValue}>{t21}</Typography>;
    $[28] = t21;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  let t23;
  if ($[30] !== dropdownOpen) {
    t23 = <span className={styles.dropdownChevron} aria-hidden={true}>{dropdownOpen ? <ChevronUp size={24} strokeWidth={1.5} /> : <ChevronDown size={24} strokeWidth={1.5} />}</span>;
    $[30] = dropdownOpen;
    $[31] = t23;
  } else {
    t23 = $[31];
  }
  let t24;
  if ($[32] !== t22 || $[33] !== t23) {
    t24 = <span className={styles.dropdownTriggerContent}>{t20}{t22}{t23}</span>;
    $[32] = t22;
    $[33] = t23;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== dropdownListId || $[36] !== dropdownOpen || $[37] !== t18 || $[38] !== t24) {
    t25 = <button type="button" className={t18} aria-expanded={dropdownOpen} aria-controls={dropdownListId} aria-haspopup="listbox" onClick={t19}>{t24}</button>;
    $[35] = dropdownListId;
    $[36] = dropdownOpen;
    $[37] = t18;
    $[38] = t24;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  const t26 = dropdownOpen && <ul id={dropdownListId} className={styles.dropdownList} role="listbox" aria-label={label}>{items.map(item_2 => {
      const isActive = item_2.key === activeKey;
      const subtitle = item_2.subtitle?.trim();
      return <li key={item_2.key} className={styles.dropdownItem} role="none"><button type="button" role="option" aria-selected={isActive} className={styles.dropdownOption} onClick={() => selectSubject(item_2.key)}><span className={styles.dropdownOptionInner}>{item_2.image && <span className={styles.icon} style={{
              "--icon-url": `url(${item_2.image.src})`
            } as CSSProperties} aria-hidden={true} />}<span className={styles.dropdownOptionText}><Typography as="span" variant="buttonLg" className={styles.dropdownOptionTitle}>{item_2.title}</Typography>{subtitle && <Typography as="span" variant="tag" className={styles.dropdownOptionSubtitle}>{subtitle}</Typography>}</span></span></button></li>;
    })}</ul>;
  let t27;
  if ($[40] !== t17.dropdown || $[41] !== t25 || $[42] !== t26) {
    t27 = <div className={t17.dropdown} ref={dropdownRef}>{t25}{t26}</div>;
    $[40] = t17.dropdown;
    $[41] = t25;
    $[42] = t26;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] !== label || $[45] !== t12.nav || $[46] !== t16 || $[47] !== t27) {
    t28 = <nav className={t12.nav} aria-label={label}>{t16}{t27}</nav>;
    $[44] = label;
    $[45] = t12.nav;
    $[46] = t16;
    $[47] = t27;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  return t28;
}
function _temp3(open) {
  return !open;
}
function _temp2(section_0) {
  return section_0 != null;
}
function _temp(item) {
  return document.getElementById(sectionIdForKey(item.key));
}